import * as React from 'react';
import { Link } from 'react-router-dom';
import Menu from '@mui/material/Menu';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import LeftNav from 'components/LeftNav';
import { baseUrl } from 'settings/index';
import type { NavLinkProps, MobileNavLinkProps } from './NavLink.types';
import {
  NavIconWrapper,
  MobileItemName,
  MobileNavLinkContainer,
  NavItemText,
  SubNavItemText,
} from './NavLink.style';

interface NavLinkSubcomponents {
  Mobile: React.FC<MobileNavLinkProps>;
}

const getIconState = (
  selected: boolean,
  icon?: React.ReactElement<any>,
  solidIcon?: React.ReactElement<any>,
) =>
  selected
    ? { color: (props) => props.theme.palette.primary.hundred, icon: solidIcon }
    : { color: (props) => props.theme.palette.neutral.sixty, icon };

const MobileNavLink: React.FC<MobileNavLinkProps> = ({ item, selected = false }) => {
  const linkTo = item.path ? `${item.path}` : '';
  const iconState = getIconState(selected, item.icon, item.solidIcon);
  const isExternalUrl = item?.isExternalUrl;

  const containerProps = isExternalUrl
    ? {
        as: 'a',
        href: linkTo,
        rel: 'noopener noreferrer',
        target: '_blank',
        'aria-label': `${item.name} (opens in a new tab)`,
      }
    : {
        to: linkTo,
        'aria-label': item.name,
      };

  return (
    <MobileNavLinkContainer {...containerProps} selected={selected}>
      <NavIconWrapper color={iconState.color}>{iconState.icon}</NavIconWrapper>
      <MobileItemName>{item.name}</MobileItemName>
    </MobileNavLinkContainer>
  );
};

const NavLink: React.FC<NavLinkProps> & NavLinkSubcomponents = ({ item, selected }) => {
  const { path = '', name } = item;
  const pathname = `${path}`;
  const getIsSelected = (currentItem) => {
    if (currentItem.children) {
      return currentItem.children
        .map((child) => child.path)
        .some((childPath) => selected.includes(childPath) && selected.indexOf(childPath) === 0);
    }
    return name !== 'Home'
      ? selected.includes(pathname) && selected.indexOf(pathname) === 0
      : selected === pathname;
  };

  const linkTo = path === 'mysearch' ? `${baseUrl}/${path}` : `${path}`;
  const iconState = getIconState(getIsSelected(item), item.icon, item.solidIcon);
  const isExternalUrl = item?.isExternalUrl;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const triggerRef = React.useRef<HTMLDivElement>(null);
  const menuId = `menu-${name.replace(/\s+/g, '-').toLowerCase()}`;
  const buttonId = `${menuId}-button`;

  const handleClick = (event: React.MouseEvent | React.KeyboardEvent) => {
    if (anchorEl) {
      setAnchorEl(null);
    } else {
      setAnchorEl((event as React.MouseEvent<HTMLElement>).currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTimeout(() => {
      triggerRef.current?.focus();
    }, 0);
  };

  const renderItem = () => (
    <LeftNav.Item>
      <LeftNav.Icon>
        <NavIconWrapper color={iconState.color}>{iconState.icon}</NavIconWrapper>
      </LeftNav.Icon>
      <NavItemText selected={getIsSelected(item)}>{item.name}</NavItemText>
      {item.children && (
        <Menu
          id={menuId}
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{ 'aria-labelledby': buttonId }}
        >
          <MenuList sx={{ display: 'flex', flexDirection: 'column', padding: 0, margin: 0 }}>
            {item.children.map((child) => (
              <MenuItem
                key={child.key}
                component={Link}
                to={child.path}
                onClick={handleClose}
                aria-label={child.name}
                sx={{ padding: '8px 16px' }}
              >
                <SubNavItemText>{child.name}</SubNavItemText>
              </MenuItem>
            ))}
          </MenuList>
        </Menu>
      )}
    </LeftNav.Item>
  );

  if (isExternalUrl) {
    return (
      <a
        href={linkTo}
        className='lnav-link'
        rel='noopener noreferrer'
        target='_blank'
        aria-label={`${name} (opens in a new tab)`}
      >
        {renderItem()}
      </a>
    );
  }

  if (item.children) {
    return (
      <div
        className='lnav-link'
        ref={triggerRef}
        onClick={handleClick}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            handleClick(e);
          }
        }}
        role='button'
        tabIndex={0}
        aria-haspopup='menu'
        aria-expanded={open}
        aria-controls={menuId}
        aria-label={name}
        id={buttonId}
        style={{ display: 'flex', flexDirection: 'column' }}
      >
        {renderItem()}
      </div>
    );
  }

  return (
    <Link to={linkTo} className='lnav-link' aria-label={name}>
      {renderItem()}
    </Link>
  );
};

NavLink.Mobile = MobileNavLink;
export default NavLink;
